import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import ModalProvider from "@/components/helpers/Modal.tsx";
import { IconQuit } from "@/components/icons";
import QuitModal from "@/components/modals/QuitModal.tsx";

import SOCIALS from "@/data/social-links.json";
import { useStaffNavigationLinks } from "@/hooks/ui/useStaffNavigationLinks.ts";
import { cn } from "@/utils/cn.ts";

const StaffNavigation = ({ className }: { className?: string }): JSX.Element => {
  const { pathname } = useLocation();
  const navigationLinks = useStaffNavigationLinks();
  const { t } = useTranslation();

  return (
    <div className={cn("h-full", className)}>
      <h1
        className={"hidden text-[2.75rem]/normal font-semibold max-lg:mt-7 max-lg:block max-lg:text-3xl/10 max-sm:mt-0"}
      >
        {t("settings")}
      </h1>
      <div className={cn("max-lg:rounded-7.5 max-lg:py-2")}>
        <div
          className={cn(
            "flex flex-col gap-4 rounded-7.5 text-lg leading-[1.125rem]",
            " max-lg:bg-transparent max-lg:px-0 max-lg:py-0",
          )}
        >
          <nav className={"text-grey-1"}>
            <ul className={""}>
              {navigationLinks.map(({ title, IconComponent, to }, index) => (
                <li key={index}>
                  <Link
                    to={to}
                    aria-label={title}
                    className={cn(
                      "group flex items-center justify-between rounded-[0.625rem] py-3 transition-colors hover:bg-white-opacity-1 max-lg:text-on-primary",
                      "hover:text-on-primary",
                      "max-lg:px-0 max-lg:py-4",
                      pathname === to && "text-primary",
                    )}
                  >
                    <div className={"flex items-center "}>
                      <IconComponent
                        className={cn(
                          "mx-[0.9375rem] h-6 w-6 fill-grey-1 transition-colors group-hover:fill-on-primary  max-lg:fill-on-primary ",
                          "max-lg:fill-on-primary",
                          pathname === to && "fill-primary",
                        )}
                      />
                      <span>{title}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          <hr className={"h-px w-full border-surface-container"} />
          <ModalProvider<boolean> initialState={false}>
            {({ modalState, setModalState }) => (
              <>
                <button
                  type={"button"}
                  aria-label={t("Actions.quit")}
                  onClick={() => setModalState(true)}
                  className={cn(
                    "group flex items-center rounded-[0.625rem] py-[0.625rem] text-[#97000B] transition-colors  hover:text-[#BA191A]",
                    "lg:hover:bg-white-opacity-1",
                    "max-lg:py-4 max-lg:text-[#97000B]",
                  )}
                >
                  <IconQuit
                    className={
                      "mx-[0.9375rem] size-5 rotate-180 stroke-[#97000B] transition-colors group-hover:stroke-[#BA191A]"
                    }
                  />
                  <span>{t("Actions.quit")}</span>
                </button>
                <QuitModal isOpen={modalState} onClose={() => setModalState(false)} />
              </>
            )}
          </ModalProvider>
        </div>
        <Link
          to={SOCIALS.TELEGRAM.LINK}
          aria-label={t("social.telegram-bot")}
          target={"_blank"}
          rel={"nofollow"}
          className={cn(
            "mt-6 flex w-full flex-col items-center rounded-[0.625rem] border-2 border-surface-container px-[0.938rem] py-[0.625rem] font-medium leading-[1.625rem] text-on-primary transition-colors  hover:border-white-opacity-1 hover:bg-white-opacity-1",
            "max-lg:flex-row max-lg:gap-1 max-lg:font-normal",
          )}
        >
          <span>{t("support")}:</span>
          <span>{SOCIALS.TELEGRAM.PROFILE}</span>
        </Link>
      </div>
    </div>
  );
};

export default StaffNavigation;
